/**
 * Доля хвоста публичного рендерера: вид `countdown` (обратный отсчёт).
 *
 * WEB-2920, заход 1: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'countdown'` и хуки остаются в фасаде; ветка вида — функция
 * отрисовки `renderCountdown(ctx)`, замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`). Эту долю импортирует только фасад: она едет в
 * ленивом куске `extended`, а не в первом экране (самопроверка обхода).
 */
import React, { Fragment, useEffect, useState } from 'react';
import type { VariableContext } from '@/player/variable-context';
import {
  blockTextRoles,
  type TextRoleStyle,
} from '../semanticTextRoles';
import { resolveFontPair } from '@/themes/fonts';
import { useEditorPreview } from '../EditorPreviewContext';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import {
  COUNTDOWN_FORMAT_DIGIT_SIZE,
  COUNTDOWN_MONO_FAMILY,
  countdownLabelsFor,
  resolveCountdownFormat,
  usePublicCountdownTarget,
  resolveLiveCountdownTarget,
  useCountdown,
} from '../countdown';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

interface CountdownBlockProps {
  /** WEB-890: id блока — якорь sessionStorage для относительного таймера. */
  blockId?: string;
  /** WEB-890: относительная длительность (мин), когда targetDate не задан. */
  durationMinutes?: number;
  targetDate?: string;
  /** WEB-696: символ между блоками цифр (absent = без разделителя). */
  separator?: string;
  /** WEB-696: цвет цифр (absent = палитра, как раньше). */
  digitColor?: string;
  /** WEB-696: id шрифта цифр из FUNNEL_FONT_IDS (WEB-672, единый набор). */
  digitFontId?: string;
  /** WEB-696 QA: размер шрифта цифр в px (absent = размер формата). */
  digitSizePx?: number;
  /**
   * WEB-1692, юнит 2: накладка на ПОДПИСИ под цифрами (роль `Secondary` общего
   * механизма ролей). Пустой объект = сегодняшний вид до пикселя: спредится
   * ПОСЛЕ прежних строк, и при пустых полях inline-стиль не меняется ни на
   * байт. Цифрам накладка не достаётся — у них свои поля выше.
   */
  secondaryStyle?: TextRoleStyle;
  /** WEB-925 атом 4: id стиль-пака — формат таймера резерва. */
  stylePackId?: string;
  /** WEB-785: локаль воронки — подписи под цифрами следуют её языку. */
  varCtx?: VariableContext;
}

function CountdownBlock({
  blockId,
  targetDate,
  durationMinutes,
  separator,
  digitColor,
  digitFontId,
  digitSizePx,
  secondaryStyle,
  stylePackId,
  varCtx,
}: CountdownBlockProps) {
  // WEB-978 п.6: тот же залип на 00:00:00, что чинили в PromoTicketBlock —
  // resolveCountdownTarget читает якорь sessionStorage по blockId, тот же id
  // переиспользуется, пока автор работает над воронкой в превью, и
  // просроченный якорь стопорит отсчёт навсегда. В превью — живой отсчёт от
  // «сейчас», без sessionStorage; публика не меняется байт-в-байт.
  const { isEditorPreview, frozen } = useEditorPreview();
  const [liveTargetMs, setLiveTargetMs] = useState<number | null>(() =>
    isEditorPreview
      ? resolveLiveCountdownTarget(targetDate, durationMinutes)
      : null,
  );
  useEffect(() => {
    if (!isEditorPreview) return;
    setLiveTargetMs(resolveLiveCountdownTarget(targetDate, durationMinutes));
  }, [isEditorPreview, targetDate, durationMinutes]);
  // WEB-3673: публика — якорь от показа человеку (прогретая SDK страница
  // `?preload=1` до показа стоит на полном сроке и якорь не пишет).
  const publicTarget = usePublicCountdownTarget(
    blockId,
    targetDate,
    durationMinutes,
    !isEditorPreview,
  );
  const targetMs = isEditorPreview ? liveTargetMs : publicTarget.targetMs;
  // WEB-2996: замороженный рендер (узел графа) — отсчёт стоит.
  const time = useCountdown(targetMs, frozen || publicTarget.waiting);

  // WEB-925 атом 4: формат таймера резолвится ИЗ ПАКА прямо в рендерере —
  // это решение о разметке (есть подписи или нет), сырой enum в CSS-переменной
  // был бы мёртвым полем (шрам shape.shadow). Пака нет / timer: 'none' →
  // 'default' = сегодняшний вид, ничего не меняется.
  const format = resolveCountdownFormat(stylePackId);
  const isMono = format === 'mono-header';
  const labels = countdownLabelsFor(
    format,
    getPublicQuizStrings(varCtx?.system?.locale),
  );

  const pad = (n: number) => String(n).padStart(2, '0');
  const digitStyle: React.CSSProperties = {
    fontSize: digitSizePx ?? COUNTDOWN_FORMAT_DIGIT_SIZE[format],
    fontWeight: 700,
    color: digitColor || 'var(--color-error, #ff4d4f)',
    lineHeight: 1,
    ...(isMono ? { fontFamily: COUNTDOWN_MONO_FAMILY } : {}),
    // Шрифт цифр автора важнее моно-шрифта формата.
    ...(digitFontId
      ? { fontFamily: resolveFontPair(digitFontId).headingFamily }
      : {}),
  };
  const values = [time.days, time.hours, time.minutes, time.seconds];
  // 12min: моно-строка компактная — дни показываем только когда они есть.
  const monoValues = time.days > 0 ? values : values.slice(1);
  // Без явного разделителя моно-строка всё равно нуждается в двоеточиях.
  const monoSeparator = separator || ':';

  if (isMono) {
    return (
      <div
        data-testid="countdown"
        data-timer-format={format}
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          marginBottom: 16,
        }}
      >
        {monoValues.map((value, i) => (
          <Fragment key={i}>
            {i > 0 && (
              <div
                data-testid="countdown-separator"
                style={digitStyle}
                aria-hidden="true"
              >
                {monoSeparator}
              </div>
            )}
            <div data-testid="countdown-digit" style={digitStyle}>
              {pad(value)}
            </div>
          </Fragment>
        ))}
      </div>
    );
  }

  return (
    <div
      data-testid="countdown"
      data-timer-format={format}
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 16,
        marginBottom: 16,
      }}
    >
      {values.map((value, i) => (
        <Fragment key={labels[i]}>
          {i > 0 && separator && (
            <div
              data-testid="countdown-separator"
              style={{ ...digitStyle, alignSelf: 'flex-start' }}
              aria-hidden="true"
            >
              {separator}
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <div data-testid="countdown-digit" style={digitStyle}>
              {pad(value)}
            </div>
            <div
              data-testid="countdown-label"
              style={{
                fontSize: 12,
                color: 'var(--color-text-secondary, #8c8c8c)',
                marginTop: 4,
                // WEB-1692: накладка автора — ПОСЛЕ прежних строк; пусто = {}.
                ...(secondaryStyle ?? {}),
              }}
            >
              {labels[i]}
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

/** Ветка `countdown` фасада — тело `case` слово в слово. */
export function renderCountdown(ctx: ExtendedRenderContext): React.ReactElement {
  const { block, stylePackId, varCtx } = ctx;
  return (
    <CountdownBlock
      blockId={block.id}
      targetDate={block.targetDate}
      durationMinutes={block.countdownMinutes}
      separator={block.countdownSeparator}
      digitColor={block.countdownDigitColor}
      digitFontId={block.countdownDigitFontId}
      digitSizePx={block.countdownDigitSizePx}
      // WEB-1692: подписи под цифрами — роль `Secondary` общего
      // механизма; роли `Title` у блока нет (цифры настраиваются
      // старшими полями выше), поэтому берём только вторичную накладку.
      secondaryStyle={blockTextRoles(block, 'countdown').secondary}
      stylePackId={stylePackId}
      varCtx={varCtx}
    />
  );
}
